<template>
  <div class="article-above-body__wrapper">
    <a-breadcrumbs
      v-bind="breadcrumbsProps"
      :visible-from="$breakpoint.mobile"
      class="article-above-body__breadcrumbs"
    >
      <a-visibility v-if="showDisclaimer" show :from="$breakpoint.desktopMd">
        <a-disclaimer
          :layout-ordering="$options.consts.topDisclaimerLayoutOrdering"
          class="article-above-body__top-disclaimer"
        />
      </a-visibility>
    </a-breadcrumbs>
    <a-thought-leadership-logo
      v-if="thoughtLeadershipLogo"
      :logo="thoughtLeadershipLogo"
      :company="linkedCompany"
      class="article__tl-logo"
    />
    <h1 class="article-above-body__article-title">
      {{ article.title }}
    </h1>

    <a-hidden-time-for-crawlers :time="article.publishedOn" />

    <div class="article-above-body__timestamp-and-disclaimer-wrapper">
      <span>
        <a-obfuscated-display-time
          :time="article.publishedOn"
          class="article-above-body__timestamp"
        />
        <span class="article-above-body__publisher-details"
          >by
          <a-authors :article="article" />
        </span>
      </span>
      <a-visibility
        v-if="showDisclaimer"
        show
        :on="[$breakpoint.tablet, $breakpoint.desktopSm]"
      >
        <a-disclaimer
          :layout-ordering="$options.consts.topDisclaimerLayoutOrdering"
        />
      </a-visibility>
    </div>
    <a-tldr
      :tldr="article.tldr"
      :font-bold="false"
      class="article-above-body__tldr-wrapper"
    />
    <div class="article-above-body__labels-container">
      <a-sticker
        v-if="article.sticker"
        :sticker="article.sticker"
        class="article-above-body__sticker"
      />
      <nav class="article-above-body__labels">
        <a-category
          class="article-above-body__category"
          v-bind="categoryProps"
          show-icon-if-category-special
        />
        <a-tag v-if="article.mainTag" :tag="article.mainTag" />
      </nav>
    </div>
    <a-media
      show-caption
      :image="article.featuredImage"
      :url="prop('url', article.featuredImageLink)"
      :directives="prop('relationDirectives', article.featuredImageLink)"
      :open-in-new-tab="prop('openInNewTab', article.featuredImageLink)"
      :image-aspect-ratio="$options.consts.aspectRatios.noRatio"
      :image-container-aspect-ratio="$options.consts.aspectRatios.article"
      :video="prop('featuredVideo', article)"
      :video-aspect-ratio="$options.consts.aspectRatios.article"
      :alt="article.title"
      :srcset-location="srcsetLocation"
      viewport-optimization
      :enlargeable-image="true"
      class="article-above-body__featured-media"
      @enlarge="$emit('enlarge', $event)"
    />
  </div>
</template>

<script>
import { prop } from 'ramda'

import { PROP_TYPES, propValidator } from '@/utils/validators'
import mixins from '@/utils/mixins'
import { ROUTE_NAME } from 'enums/routes'
import ADisclaimer from 'shared/ADisclaimer'
import { LAYOUT as DISCLAIMER_LAYOUT } from 'shared/ADisclaimer/enums'
import ABreadcrumbs, { BREADCRUMB_NAMING } from 'shared/ABreadcrumbs'
import AThoughtLeadershipLogo from 'shared/AThoughtLeadershipLogo'
import ATldr from 'shared/ATldr'
import ATag from 'shared/ATag'
import ACategory from 'shared/ACategory'
import ASticker from 'shared/ASticker'
import AMedia from 'shared/AMedia'
import { SRCSET_LOCATION } from 'enums/images'
import { aspectRatios } from 'enums/aspectRatios'
import AAuthors from 'shared/AAuthors'
import AHiddenTimeForCrawlers from 'shared/PublishTime/AHiddenTimeForCrawlers/index.vue'
import AObfuscatedDisplayTime from 'shared/PublishTime/AObfuscatedDisplayTime/index.vue'

export default {
  name: 'ArticleAboveContent',
  mixins: [mixins.urlGenerators],
  components: {
    AObfuscatedDisplayTime,
    AHiddenTimeForCrawlers,
    AAuthors,
    AMedia,
    ASticker,
    ACategory,
    ATag,
    ATldr,
    AThoughtLeadershipLogo,
    ABreadcrumbs,
    ADisclaimer
  },
  props: {
    article: propValidator([PROP_TYPES.OBJECT]),
    showDisclaimer: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  consts: {
    topDisclaimerLayoutOrdering: [DISCLAIMER_LAYOUT.BOTTOM_LEFT],
    aspectRatios
  },
  computed: {
    breadcrumbsDynamicNames() {
      const isArticleWithSubcategory = [
        ROUTE_NAME.CATEGORY_SUBCATEGORY_ARTICLE,
        ROUTE_NAME.CATEGORY_EDUCATION_CENTER_ARTICLE
      ].includes(this.$route.name)

      const { categoryName, subcategoryName } = this.article
      const subcategoryOrArticleName = isArticleWithSubcategory
        ? subcategoryName
        : this.article.title

      return {
        [ROUTE_NAME.CATEGORY]: categoryName,
        [ROUTE_NAME.CATEGORY_SUBCATEGORY]: subcategoryOrArticleName,
        [ROUTE_NAME.CATEGORY_EDUCATION_CENTER]: subcategoryOrArticleName,
        [ROUTE_NAME.CATEGORY_SUBCATEGORY_ARTICLE]: this.article.title,
        [ROUTE_NAME.CATEGORY_EDUCATION_CENTER_ARTICLE]: this.article.title,
        /**
         * ToDo: Doesn't work at the moment. WCR in FMP-11535
         */
        [ROUTE_NAME.LEGACY_ARTICLE_ON_CATEGORY_PATH]: this.article.title
      }
    },
    breadcrumbsProps() {
      return {
        breadcrumbsDynamicNames: this.breadcrumbsDynamicNames,
        breadcrumbsNaming: BREADCRUMB_NAMING.KEEP_AS_IS
      }
    },
    categoryProps() {
      const {
        categoryName,
        categorySlug,
        subcategoryName,
        subcategorySlug
      } = this.article

      return {
        categoryName,
        categorySlug,
        subcategoryName,
        subcategorySlug
      }
    },
    srcsetLocation() {
      return SRCSET_LOCATION.ARTICLE_PAGE
    },
    thoughtLeadershipLogo() {
      if (!this.article.sponsorshipCompany) return null

      return this.article.sponsorshipCompany.thoughtLeadershipLogo
    },
    linkedCompany() {
      if (!this.article.sponsorshipCompany) return null

      return this.article.sponsorshipCompany.linkedCompany
    }
  },
  methods: {
    prop
  }
}
</script>

<style scoped lang="scss">
@import '~assets/scss/local/styles/article';
@import '~assets/scss/global/mixins/figures';

.article-above-body__wrapper {
  .article-above-body__breadcrumbs {
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 20px;

    @include mobile {
      margin-bottom: 0;
    }
  }

  /deep/ .breadcrumbs__list .breadcrumbs__item:last-child {
    @include mobile {
      display: none;
    }
  }

  .article-above-body__top-disclaimer {
    padding-left: 40px;
  }

  .article-above-body__article-title {
    @extend .article-title;
  }

  .article-above-body__timestamp-and-disclaimer-wrapper {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-top: 10px;
  }

  .article-above-body__timestamp,
  .article-above-body__publisher-details {
    @extend .timestamp;
  }

  .article-above-body__tldr-wrapper {
    margin-top: 20px;

    /deep/ li.tldr__item {
      @extend .tldr;
    }

    @include mobile {
      /deep/ li.tldr__item {
        @include bullet($line-height: 16px, $size: 5px, $left: 12px);
      }
    }
  }

  .article-above-body__labels-container {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    margin-top: 20px;

    .article-above-body__sticker {
      margin-right: 20px;
    }

    .article-above-body__labels {
      display: flex;
      flex-wrap: wrap;
      font-size: 0;
    }

    .article-above-body__category {
      margin-right: 15px;
    }
  }

  .article-above-body__featured-media {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}
</style>
